/**
 * Standalone marks layer. Waypoints that are not part of a route live here:
 * each is drawn with its own icon and name. When a "go to" is active, this layer
 * also draws the magenta active-leg line from the boat to the target mark, the
 * same treatment a followed route's active leg gets.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';
import type { Waypoint } from '@/routing';
import { circleRing } from '@/safety';
import { getPalette } from './style/palettes';
import type { Palette } from '@/store';

const SOURCE_ID = 'marks';

/**
 * The marks layer ids, bottom -> top in draw order, and the order we re-seat them
 * in so a dropped mark is never buried under the async-loaded vector chart cells
 * (same race the ownship marker hit: create the layer before the cells finish
 * loading and addCellToMap stacks the opaque fills on top). Missing layers skip.
 */
const MARKS_LAYER_IDS = [
  'marks-goto-leg',
  'marks-goto-label',
  'marks-ring',
  'marks-icon',
  'marks-label',
] as const;

/** Lift the marks layers to the top of the stack, in draw order. */
export function raiseMarksLayers(map: MaplibreMap): void {
  for (const id of MARKS_LAYER_IDS) {
    if (map.getLayer(id)) map.moveLayer(id);
  }
}

/** Boat -> target line drawn while a goto is active. */
export interface GotoLeg {
  boat: [number, number];
  target: [number, number];
  /** Name of the mark we are going to, so the line can label itself. */
  targetName?: string;
}

function build(marks: Waypoint[], gotoLeg?: GotoLeg | null): FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  if (gotoLeg) {
    const name = (gotoLeg.targetName ?? '').trim();
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [gotoLeg.boat, gotoLeg.target] },
      // `label` is written along the line so the magenta line says what it is
      // ("Go to <mark>") instead of being an unexplained line from the boat.
      properties: { kind: 'goto-leg', name, label: name ? `Go to ${name}` : 'Go to' },
    });
  }
  for (const m of marks) {
    if (typeof m.arrivalRadiusNm === 'number' && m.arrivalRadiusNm > 0) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [circleRing(m.position, m.arrivalRadiusNm)] },
        properties: { kind: 'mark-ring', markId: m.id, cLon: m.position[0], cLat: m.position[1] },
      });
    }
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: m.position },
      properties: { kind: 'mark', markId: m.id, name: m.name ?? '', icon: m.icon ?? 'plot-waypoint' },
    });
  }
  return { type: 'FeatureCollection', features };
}

export function ensureMarksLayer(
  map: MaplibreMap,
  marks: Waypoint[],
  palette: Palette,
  gotoLeg?: GotoLeg | null,
): void {
  const p = getPalette(palette);
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data: build(marks, gotoLeg) });
  } else {
    (map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(build(marks, gotoLeg));
  }

  if (!map.getLayer('marks-goto-leg')) {
    map.addLayer({
      id: 'marks-goto-leg',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'goto-leg'],
      paint: { 'line-color': p.CHMGD, 'line-width': 2.6 },
    });
  }
  // Name the go-to line along its length so it explains itself at a glance.
  if (!map.getLayer('marks-goto-label')) {
    map.addLayer({
      id: 'marks-goto-label',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'goto-leg'],
      layout: {
        'symbol-placement': 'line',
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Regular'],
        'text-size': 11,
        'text-offset': [0, -0.8],
        'text-keep-upright': true,
      },
      paint: { 'text-color': p.CHMGD, 'text-halo-color': p.UI_BG, 'text-halo-width': 1.6 },
    });
  }
  if (!map.getLayer('marks-ring')) {
    map.addLayer({
      id: 'marks-ring',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'mark-ring'],
      paint: { 'line-color': p.CHMGD, 'line-width': 1.2, 'line-dasharray': [3, 2], 'line-opacity': 0.85 },
    });
  }
  if (!map.getLayer('marks-icon')) {
    map.addLayer({
      id: 'marks-icon',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'mark'],
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-size': 0.85,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });
  }
  if (!map.getLayer('marks-label')) {
    map.addLayer({
      id: 'marks-label',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'mark'],
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Open Sans Regular'],
        'text-size': 11,
        'text-offset': [0.9, 0],
        'text-anchor': 'left',
        'text-optional': true,
      },
      paint: { 'text-color': p.UI_ACCENT, 'text-halo-color': p.UI_BG, 'text-halo-width': 1.4 },
    });
  }

  // Keep dropped marks above the chart cells, whether created now or earlier (a
  // mark placed before the async cells loaded would otherwise sit under them).
  raiseMarksLayers(map);
}

export function updateMarksLayer(
  map: MaplibreMap,
  marks: Waypoint[],
  gotoLeg?: GotoLeg | null,
): void {
  const src = map.getSource(SOURCE_ID);
  if (src) (src as maplibregl.GeoJSONSource).setData(build(marks, gotoLeg));
}
